export enum CMWorkflowStatus {
  PRODUCT_INFO_GENERATED = 'PRODUCT_INFO_GENERATED',
  CP_OPT_RESTRUCTURED = 'CP_OPT_RESTRUCTURED',
  WP_UPLOADED = 'WP_UPLOADED',
  IMG_PROCESSED = 'IMG_PROCESSED',
  CM_REQUESTED = 'CM_REQUESTED',
  CM_PUBLISHED = 'CM_PUBLISHED',
  DELETED = 'DELETED',
}

export enum CMRequestReason {
  NEW_MATCH = 'NEW_MATCH',
  THU_IMAGE_ISSUE = 'THU_IMAGE_ISSUE',
  PDP_IMAGE_ISSUE = 'PDP_IMAGE_ISSUE',
  PRODUCT_INFO_INCORRECT = 'PRODUCT_INFO_INCORRECT',
  PRODUCT_DISCONTINUED = 'PRODUCT_DISCONTINUED',
  PRODUCT_OBSOLETE = 'PRODUCT_OBSOLETE',
  PRODUCT_OUT_OF_STOCK = 'PRODUCT_OUT_OF_STOCK',
  BETTER_SOURCE_FOUND = 'BETTER_SOURCE_FOUND',
}

export interface WorkflowStepLabels {
  id: CMWorkflowStatus;
  labelBefore: string;
  labelAfter: string;
  description: string;
  requirement?: string;
}

export const WORKFLOW_STEP_LABELS: WorkflowStepLabels[] = [
  {
    id: CMWorkflowStatus.PRODUCT_INFO_GENERATED,
    labelBefore: 'Product Info Generate',
    labelAfter: 'Product Info Generated',
    description: 'Product information translation and generation',
    requirement: 'Product info must be validated & translated',
  },
  {
    id: CMWorkflowStatus.CP_OPT_RESTRUCTURED,
    labelBefore: 'CP Options Restructure',
    labelAfter: 'CP Options Restructured',
    description: 'Transform Coupang product options into SkinSeoul product structure',
    requirement: 'CP Options must be restructured and saved',
  },
  {
    id: CMWorkflowStatus.WP_UPLOADED,
    labelBefore: 'WP Upload',
    labelAfter: 'WP Uploaded',
    description: 'Upload product to WordPress and generate SKUs',
    requirement: 'SKUs must be generated after uploading products',
  },
  {
    id: CMWorkflowStatus.IMG_PROCESSED,
    labelBefore: 'Images Processing',
    labelAfter: 'Images Processed',
    description: 'Manage and verify product images (thumbnails and PDP images)',
    requirement: 'SKU mappings must exist and images must be uploaded using media sync',
  },
  {
    id: CMWorkflowStatus.CM_REQUESTED,
    labelBefore: 'CM Request',
    labelAfter: 'CM Requested',
    description: 'Catalog Management request',
    requirement: 'Above requirements must be met for catalog management request',
  },
  {
    id: CMWorkflowStatus.CM_PUBLISHED,
    labelBefore: 'CM Publish',
    labelAfter: 'CM Published',
    description: 'Publish product to live site',
  },
];

export interface SkuCpiiMapping {
  sku: string;
  cpii: string;
  type: string;
}

export interface StatusHistoryEntry {
  toStatus: string;
  fromStatus?: string;
  operator: string;
  updatedAt: string;
  reason?: string;
  message?: string;
}

export interface CMWorkflowItem {
  gpi: string;
  cppi?: string;
  skuCpiiMappings?: SkuCpiiMapping[];
  skus?: string[];
  type?: string;
  cmStatus: CMWorkflowStatus;
  cmStatusHistory?: StatusHistoryEntry[];
  lastHistory?: StatusHistoryEntry;
  month?: string;
  wave?: string;
  createdAt: string;
  modifiedAt: string;
}

export const WORKFLOW_ORDER = [
  CMWorkflowStatus.PRODUCT_INFO_GENERATED,
  CMWorkflowStatus.CP_OPT_RESTRUCTURED,
  CMWorkflowStatus.WP_UPLOADED,
  CMWorkflowStatus.IMG_PROCESSED,
  CMWorkflowStatus.CM_REQUESTED,
  CMWorkflowStatus.CM_PUBLISHED,
];

// Legacy statuses no longer in the enum but still possible in DB records.
// Map them to the closest equivalent in the current 6-step workflow so
// pre-existing workflows continue to render correctly without a DB migration.
const LEGACY_STATUS_MAP: Record<string, CMWorkflowStatus> = {
  QA_VERIFIED: CMWorkflowStatus.CM_REQUESTED,
  DELEO_UPLOADED: CMWorkflowStatus.CM_REQUESTED,
};

export const normalizeWorkflowStatus = (status: string | undefined): CMWorkflowStatus | undefined => {
  if (!status) {
    return undefined;
  }
  return LEGACY_STATUS_MAP[status] ?? (status as CMWorkflowStatus);
};

export const isWorkflowStepCompleted = (
  cmWorkflowData: CMWorkflowItem[] | undefined,
  targetStatus: CMWorkflowStatus,
): boolean => {
  if (!cmWorkflowData || cmWorkflowData.length === 0) {
    return false;
  }

  const currentStatus = normalizeWorkflowStatus(cmWorkflowData[0]?.cmStatus);
  if (!currentStatus) {
    return false;
  }

  const currentIndex = WORKFLOW_ORDER.indexOf(currentStatus);
  const targetIndex = WORKFLOW_ORDER.indexOf(targetStatus);

  return currentIndex >= targetIndex;
};
